import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Download, RefreshCw } from "lucide-react";
import { api } from "../../services/api";
import recordOperationsDownload from '../../utils/recordOperationsDownload';

// Fase 3 del puente Almacén–Compras: "habilitar el reporte de cuánto
// consume cada área mes a mes". Agrupa las salidas y consumos (los dos
// tipos de movimiento que representan consumo real, no traslados ni
// entradas) por centro de costo, para el mes elegido, mostrando cantidad
// de movimientos, unidades totales y un valor estimado (unidades × costo
// promedio actual del material — ver Fase 2, materials.avg_cost).
//
// El valor es "estimado" a propósito: no se guarda el costo unitario en
// cada salida (solo en las entradas, que sí saben el precio pagado), así
// que se usa el costo promedio ACTUAL del material, no el que tenía en el
// momento exacto de esa salida.

const formatMoney = (n) => {
  const v = Number(n || 0);
  try {
    return v.toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
  } catch (_) {
    return '$' + String(Math.round(v)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }
};

const currentMonthYM = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthRange = (ym) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || '').trim());
  if (!m) return { from: '', to: '' };
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const from = `${y}-${String(mo).padStart(2, '0')}-01`;
  const lastDay = new Date(y, mo, 0).getDate();
  const to = `${y}-${String(mo).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { from, to };
};

export function CostCenterReportModule() {
  const [month, setMonth] = useState(currentMonthYM());
  const [loading, setLoading] = useState(false);
  const [movements, setMovements] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.accounting.costCenters.list({ limit: 2000, order: 'asc' });
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (active) setCostCenters(rows);
      } catch (_) {
        if (active) setCostCenters([]);
      }
    })();
    return () => { active = false; };
  }, []);

  const costCenterNameByCode = useMemo(() => {
    const map = new Map();
    costCenters.forEach((cc) => {
      const key = String(cc.code || cc.name || '').trim();
      if (key) map.set(key, cc.name || cc.code || key);
    });
    return map;
  }, [costCenters]);

  const load = async () => {
    const { from, to } = monthRange(month);
    if (!from || !to) return;
    setLoading(true);
    try {
      const [salidaRes, consumibleRes] = await Promise.all([
        api.movements.get({ type: 'salida', start_date: from, end_date: to, limit: 5000, order: 'desc' }),
        api.movements.get({ type: 'consumible', start_date: from, end_date: to, limit: 5000, order: 'desc' }),
      ]);
      const rowsSalida = Array.isArray(salidaRes?.data) ? salidaRes.data : [];
      const rowsConsumible = Array.isArray(consumibleRes?.data) ? consumibleRes.data : [];
      setMovements([...rowsSalida, ...rowsConsumible]);
    } catch (_) {
      setMovements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const groups = useMemo(() => {
    const map = new Map();
    let uncostedLines = 0;
    (movements || []).forEach((m) => {
      const cc = String(m.cost_center || '').trim() || 'Sin centro de costo';
      const qty = Number(m.quantity || 0) || 0;
      const avgCost = (m.material_avg_cost !== null && m.material_avg_cost !== undefined) ? Number(m.material_avg_cost) : null;
      const hasCost = avgCost !== null && avgCost > 0;
      if (!hasCost) uncostedLines += 1;
      const current = map.get(cc) || { costCenter: cc, movementsCount: 0, totalQty: 0, estimatedValue: 0, uncostedItems: 0 };
      current.movementsCount += 1;
      current.totalQty += qty;
      if (hasCost) current.estimatedValue += qty * avgCost;
      else current.uncostedItems += 1;
      map.set(cc, current);
    });
    const list = Array.from(map.values()).sort((a, b) => b.estimatedValue - a.estimatedValue);
    const totalValue = list.reduce((acc, r) => acc + r.estimatedValue, 0);
    return { list, totalValue, uncostedLines, totalLines: (movements || []).length };
  }, [movements]);

  const exportToXlsx = () => {
    try {
      const data = groups.list.map((r) => ({
        'Centro de costo': r.costCenter === 'Sin centro de costo' ? r.costCenter : (costCenterNameByCode.get(r.costCenter) ? `${r.costCenter} - ${costCenterNameByCode.get(r.costCenter)}` : r.costCenter),
        'Movimientos': r.movementsCount,
        'Unidades totales': r.totalQty,
        'Valor estimado': r.estimatedValue,
        'Ítems sin costo calculado': r.uncostedItems,
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Consumo por centro de costo');
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'almacen', reportName: 'Consumo por centro de costo' });
      XLSX.writeFile(wb, `consumo_centro_costo_${month}.xlsx`);
    } catch (err) {
      console.error('Error exportando reporte de consumo', err);
      alert('No se pudo exportar el archivo .xlsx.');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between w-full mb-6 flex-wrap gap-3">
        <div>
          <h3 className="section-title">Consumo por centro de costo</h3>
          <p className="text-xs text-gray-500 mt-1">Salidas y consumos del mes, agrupados por área — valor estimado con el costo promedio actual de cada material.</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border rounded px-3 py-2 text-sm bg-white"
          />
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 border border-gray-300 bg-white px-3 py-2 rounded hover:bg-gray-50 text-sm"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </button>
          <button
            type="button"
            onClick={exportToXlsx}
            className="inline-flex items-center gap-2 text-white px-4 py-2 rounded text-sm"
            style={{ backgroundColor: '#04873b' }}
            disabled={groups.list.length === 0}
          >
            <Download className="h-4 w-4" /> Descargar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-[11px] text-gray-500 uppercase tracking-wide">Valor total consumido (estimado)</div>
          <div className="text-xl font-semibold text-gray-900">{formatMoney(groups.totalValue)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-[11px] text-gray-500 uppercase tracking-wide">Movimientos en el mes</div>
          <div className="text-xl font-semibold text-gray-900">{groups.totalLines}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-[11px] text-gray-500 uppercase tracking-wide">Sin costo calculado</div>
          <div className="text-xl font-semibold text-gray-900">{groups.uncostedLines}</div>
          <div className="text-xs text-gray-500 mt-1">materiales que aún no reciben entradas con precio de OC</div>
        </div>
      </div>

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-700">
              <th className="px-4 py-2 text-left">Centro de costo</th>
              <th className="px-4 py-2 text-right">Movimientos</th>
              <th className="px-4 py-2 text-right">Unidades</th>
              <th className="px-4 py-2 text-right">Valor estimado</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">Cargando...</td></tr>
            )}
            {!loading && groups.list.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500">Sin salidas ni consumos registrados este mes.</td></tr>
            )}
            {!loading && groups.list.map((r) => (
              <tr key={r.costCenter} className="border-t">
                <td className="px-4 py-2">
                  {r.costCenter === 'Sin centro de costo' ? (
                    <span className="text-gray-500 italic">{r.costCenter}</span>
                  ) : (
                    <>
                      {r.costCenter}
                      {costCenterNameByCode.get(r.costCenter) ? <span className="text-gray-500"> — {costCenterNameByCode.get(r.costCenter)}</span> : null}
                    </>
                  )}
                </td>
                <td className="px-4 py-2 text-right font-mono">{r.movementsCount}</td>
                <td className="px-4 py-2 text-right font-mono">{r.totalQty}</td>
                <td className="px-4 py-2 text-right font-mono">{formatMoney(r.estimatedValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
